import { useMutation, useQuery } from "@apollo/client";
import { useCallback, useState } from "react";
import Button from "@/components/Button";
import Dialog from "@/components/Dialog";
import IconButton from "@/components/IconButton";
import TextField from "@/components/TextField";
import pluralize from "@/lib/pluralize";
import { toFormat } from "@/lib/dateHelpers";
import { showErrorSnackbar, showSnackbar } from "@/lib/snackbarUtils";
import {
  ADD_ELECTION_PAST_PRESIDENT_ASSIGNEE_MUTATION,
  GENERATE_ELECTION_PAST_PRESIDENTS_ROSTER_MUTATION,
  REMOVE_ELECTION_PAST_PRESIDENT_ASSIGNEE_MUTATION
} from "../mutations";
import { ELECTION_PAST_PRESIDENT_ASSIGNEES_QUERY, type ElectionPastPresidentAssigneeNode } from "../queries";

export type DownloadPastPresidentsRosterDialogProps = {
  open: boolean;
  electionRunId?: string;
  electionRunIds?: string[];
  electionSummary?: string;
  onClose(): void;
};

export default function DownloadPastPresidentsRosterDialog({
  open,
  electionRunId,
  electionRunIds,
  electionSummary,
  onClose
}: DownloadPastPresidentsRosterDialogProps) {
  const [newName, setNewName] = useState("");
  const [assignees, setAssignees] = useState<ElectionPastPresidentAssigneeNode[]>([]);
  const [busyAction, setBusyAction] = useState<"add" | "remove" | "download" | null>(null);

  const { loading: loadingAssignees, refetch } = useQuery(ELECTION_PAST_PRESIDENT_ASSIGNEES_QUERY, {
    skip: !open,
    fetchPolicy: "network-only",
    onCompleted: (data) => {
      setAssignees(data.electionPastPresidentAssignees);
    }
  });

  const [addAssignee] = useMutation(ADD_ELECTION_PAST_PRESIDENT_ASSIGNEE_MUTATION);
  const [removeAssignee] = useMutation(REMOVE_ELECTION_PAST_PRESIDENT_ASSIGNEE_MUTATION);
  const [generateRoster] = useMutation(GENERATE_ELECTION_PAST_PRESIDENTS_ROSTER_MUTATION);

  const runCount = electionRunIds?.length ?? (electionRunId ? 1 : 0);
  const isBusy = busyAction != null || loadingAssignees;

  const handleAdd = useCallback(async () => {
    const name = newName.trim();
    if (!name || busyAction) return;

    setBusyAction("add");
    try {
      const { data } = await addAssignee({ variables: { input: { name } } });
      const result = data?.addElectionPastPresidentAssignee;
      if (!result?.success || !result.assignee) {
        showErrorSnackbar("Could not add Past President", { subtitle: result?.message || "Please try again." });
        return;
      }
      setNewName("");
      const refreshed = await refetch();
      setAssignees(refreshed.data.electionPastPresidentAssignees);
    } catch {
      showErrorSnackbar("Could not add Past President", { subtitle: "Please try again." });
    } finally {
      setBusyAction(null);
    }
  }, [addAssignee, busyAction, newName, refetch]);

  const handleRemove = useCallback(
    async (id: string) => {
      if (busyAction) return;

      setBusyAction("remove");
      try {
        const { data } = await removeAssignee({ variables: { input: { id } } });
        const result = data?.removeElectionPastPresidentAssignee;
        if (!result?.success) {
          showErrorSnackbar("Could not remove Past President", { subtitle: result?.message || "Please try again." });
          return;
        }
        if (result.assignees) {
          setAssignees(result.assignees);
        } else {
          const refreshed = await refetch();
          setAssignees(refreshed.data.electionPastPresidentAssignees);
        }
      } catch {
        showErrorSnackbar("Could not remove Past President", { subtitle: "Please try again." });
      } finally {
        setBusyAction(null);
      }
    },
    [busyAction, refetch, removeAssignee]
  );

  const handleDownload = useCallback(async () => {
    if (busyAction || assignees.length === 0) return;

    setBusyAction("download");
    try {
      const input =
        electionRunIds && electionRunIds.length > 0
          ? {
              electionRunIds,
              pastPresidentAssigneeIds: assignees.map((row) => row.id)
            }
          : {
              electionRunId,
              pastPresidentAssigneeIds: assignees.map((row) => row.id)
            };

      const { data } = await generateRoster({ variables: { input } });
      const result = data?.generateElectionPastPresidentsRoster;

      if (!result?.success || !result.url) {
        showErrorSnackbar("Past Presidents roster not generated", {
          subtitle: result?.message || "Please try again later."
        });
        return;
      }

      showSnackbar(result.message || "Export generated");
      window.location.assign(result.url);
      onClose();
    } catch {
      showErrorSnackbar("Past Presidents roster not generated", { subtitle: "Please try again later." });
    } finally {
      setBusyAction(null);
    }
  }, [assignees, busyAction, electionRunId, electionRunIds, generateRoster, onClose]);

  const handleClosed = useCallback(() => {
    if (!isBusy) {
      setNewName("");
      onClose();
    }
  }, [isBusy, onClose]);

  return (
    <Dialog
      open={open}
      headline="Download Past Presidents roster"
      onClosed={handleClosed}
      disableClose={isBusy}
      actions={
        <>
          <Button variant="text" onClick={handleClosed} disabled={isBusy} testId="past-presidents-roster-cancel">
            Cancel
          </Button>
          <Button
            variant="filled"
            onClick={handleDownload}
            disabled={isBusy || assignees.length === 0}
            testId="past-presidents-roster-download"
          >
            Download roster
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-6">
        <p className="text-body-medium">
          {electionSummary ??
            `This export splits newly elected members across Past Presidents for ${runCount} completed ${pluralize(runCount, "election run")}. Add or remove names below, then download.`}
        </p>
        <ul className="flex flex-col gap-2" data-testid="past-presidents-assignee-list">
          {assignees.map((assignee) => (
            <li
              key={assignee.id}
              className="flex items-center justify-between gap-4 rounded-lg border border-sys-brand-outline-variant px-4 py-3"
            >
              <span className="text-body-medium">{assignee.name}</span>
              <IconButton
                name="delete"
                ariaLabel={`Remove ${assignee.name}`}
                onClick={() => handleRemove(assignee.id)}
                disabled={isBusy || assignees.length <= 1}
                data-testid={`remove-past-president-${assignee.id}`}
              />
            </li>
          ))}
          {assignees.length === 0 && !loadingAssignees ? (
            <li className="text-body-medium text-sys-brand-on-surface-variant">No Past Presidents on the list.</li>
          ) : null}
        </ul>
        <div className="flex flex-col gap-4 sm:flex-row sm:items-end">
          <TextField
            className="flex-1"
            label="Add Past President"
            value={newName}
            onChangeText={setNewName}
            disabled={isBusy}
          />
          <Button
            variant="outlined"
            onClick={handleAdd}
            disabled={isBusy || !newName.trim()}
            testId="past-presidents-add"
          >
            Add
          </Button>
        </div>
      </div>
    </Dialog>
  );
}

export function formatPastPresidentsElectionSummary(
  runs: { startedAt: string; completedAt?: string | null }[]
): string {
  if (runs.length === 1) {
    const run = runs[0];
    const started = toFormat(run.startedAt, "MMM dd, yyyy h:mm a");
    const completed = run.completedAt ? toFormat(run.completedAt, "MMM dd, yyyy h:mm a") : null;
    return `Election run started ${started ?? ""}${completed ? `, completed ${completed}` : ""}. Adjust Past Presidents below, then download the roster.`;
  }
  return `${runs.length} completed election runs will be merged into one roster. Adjust Past Presidents below, then download.`;
}
